/**
 * usePosSocket — real-time Socket.IO integration for the POS terminal.
 *
 * Connects to the /pos namespace and:
 *   • Invalidates the product/inventory cache on inventory:updated
 *
 * The socket reconnects automatically when the in-memory access token
 * is refreshed.
 */

import { WS_EVENTS } from '@burmazon/shared';
import { useSocket, useSocketEvent } from '@burmazon/shared/socket';
import type { InventoryUpdatedPayload } from '@burmazon/shared/socket';
import { useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { getAccessToken, subscribeToToken } from '../lib/api';

const SERVER_URL =
  import.meta.env.VITE_API_URL?.replace(/\/api\/v1\/?$/, '') ?? 'http://localhost:4000';

function useAccessToken(): string | null {
  return useSyncExternalStore(subscribeToToken, getAccessToken, () => null);
}

export interface UsePosSocketReturn {
  isConnected: boolean;
}

export function usePosSocket(): UsePosSocketReturn {
  const token = useAccessToken();
  const { socket, isConnected } = useSocket({
    namespace: '/pos',
    token,
    serverUrl: SERVER_URL,
    enabled: !!token,
  });

  const queryClient = useQueryClient();

  // Refresh the product grid when stock changes so availability is up-to-date
  useSocketEvent<InventoryUpdatedPayload>(socket, WS_EVENTS.INVENTORY_UPDATED, (_data) => {
    // Key must match TerminalPage/QuickKeysPanel: ['pos', 'products', …]
    void queryClient.invalidateQueries({ queryKey: ['pos', 'products'] });
    void queryClient.invalidateQueries({ queryKey: ['inventory'] });
  });

  return { isConnected };
}
